import {
    Grid,
    Typography
} from '@mui/material';
import { SubmitButton } from '@tassat/button';
import { SelectOption, TransactionTypeChar } from '@tassat/common-definitions';
import {
    CheckboxField,
    CurrencyField,
    RecipientField,
    SelectField,
    SimpleErrorMessage,
    TextField
} from '@tassat/formik-input';
import { AddressBookRecord } from '@tassat/fred-definitions';
import { AddressBookDropdown, FredAddressBookContactOptionRenderer } from '@tassat/menu';
import { ButtonType, LastTwoAssetTransactionsWidget, WalletAddress } from '@tassat/text';
import {
    assetConversionHelper,
    formatAsAsset,
    formatAsAssetNoSign,
    stripPrefix
} from '@tassat/utility';
import { Formik } from 'formik';
import moment from 'moment';
import * as React from 'react';

import { emptySelectOption } from 'Lib/constant';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAssets } from 'Selectors/common';

import { SendAssetPurposeMapped } from './SendAssetFormHelper';
import {
    DEFAULT_INITIAL_STATE,
    SendAssetFormAction,
    SendAssetFormReducer,
    SendAssetFormState
} from './SendAssetFormReducer';
import { validateSend } from './submission';
import {
    IFormErrors, IFormProps, IFormikProps, ISendFormValues
} from './types';

const FormContents = (props: IFormProps) => {
    const {
        classes,
        formProps,
        validatingAccountAddress,
        formState,
        dispatchFormAction,
        errors,
        values,
        isSubmitting,
        isValid,
        setFieldValue,
        submitForm,
        validateForm,
        setFieldError,
        accountPositionBalance,
        setFieldTouched
    } = props;

    const {
        addressBookOptions,
        currentWallet,
        getTransactionsSync,
        assetOptions
    } = formProps;
    // TODO If multiple Currency assets, update them with a filter
    const accountPositionCcyBalance = accountPositionBalance[0].ccy[values.AssetType.refData?.currency?.AssetId];
    const accountPositionSecurityBalance = accountPositionBalance[0].security[values.AssetType.refData?.security?.AssetId];

    const [ disableFiatField, setDisabledFiatField ] = React.useState(false);
    const [ disableAssetField, setDisableAssetField ] = React.useState(false);
    const [ isFiatAmountBeingUpdated, setIsFiatAmountBeingUpdated ] = React.useState(false);
    const [ isAssetAmountBeingUpdated, setIsAssetAmountBeingUpdated ] = React.useState(false);
    const assets = useAppSelector(getAssets);

    React.useEffect(() => {
        if (values.AssetType.value !== values.AssetType.refData?.security?.Ticker) return;
        if (isAssetAmountBeingUpdated) return;

        const realFiatAmount = stripPrefix(values.FiatAmount, '$');

        if (realFiatAmount === '.'){
            setFieldValue('FiatAmount', '0.');
            setFieldValue('AssetAmount', '0.');
        }

        if (disableAssetField && realFiatAmount === '') {
            setFieldValue('AssetAmount', '');
            setIsFiatAmountBeingUpdated(false);
            setDisableAssetField(false);
        }

        if (realFiatAmount !== '') {
            setDisableAssetField(true);
            setIsFiatAmountBeingUpdated(true); // Flag to prevent AssetAmount update
            const delayDebounceFn = setTimeout(() => {
                setFieldValue('AssetAmount', (assetConversionHelper(values.FiatAmount, values.AssetType.refData?.security.Price, values.AssetType.refData?.security)));
                setFieldTouched('AssetAmount');
            }, 500);

            return () => clearTimeout(delayDebounceFn);
        }
    }, [values.FiatAmount]);

    React.useEffect(() => {
        if (values.AssetType.value !== values.AssetType.refData?.security?.Ticker) return;
        if (isFiatAmountBeingUpdated) return;

        const realAssetAmount = stripPrefix(values.AssetAmount, values.AssetType.refData?.security?.Ticker ?? '');

        if (realAssetAmount === '.'){
            setFieldValue('FiatAmount', '0.');
            setFieldValue('AssetAmount', '0.');
        }

        if (disableFiatField && realAssetAmount.length <= 1) {
            setFieldValue('FiatAmount', '');
            setIsAssetAmountBeingUpdated(false);
            setDisabledFiatField(false);
        }

        if (realAssetAmount.length > 1) {
            setDisabledFiatField(true);
            setIsAssetAmountBeingUpdated(true); // Flag to prevent FiatAmount update
            const delayDebounceFn = setTimeout(() => {
                setFieldValue('FiatAmount', assetConversionHelper(values.AssetAmount, values.AssetType.refData?.security.Price, values.AssetType.refData?.currency));
            }, 500);

            return () => clearTimeout(delayDebounceFn);
        }
    }, [values.AssetAmount]);

    React.useEffect(() => {
        setDisabledFiatField(false);
        setDisableAssetField(false);
        setFieldValue('AssetAmount', '');
        setFieldValue('FiatAmount', '');
    }, [ values.AssetType.value, setFieldValue ]);

    React.useEffect(() => {
        setFieldValue('isFiatAmount', disableAssetField);
    }, [ disableAssetField, setFieldValue ]);

    if (validatingAccountAddress && !errors.RecipientAddress) {
        setFieldError('RecipientAddress', 'Validating...');
    }

    const filteredContacts: SelectOption[] = React.useMemo(
        () => addressBookOptions.filter(
            (contact) => currentWallet &&
                (contact.value as AddressBookRecord).AccountId !== currentWallet.AccountId
        ), [ addressBookOptions, currentWallet ]
    );

    const handleCopy = () => {
        // eslint-disable-next-line no-undef
        let valueToCopy = '-';
        const walletAddress = values?.Recipient?.value.Account;
        if (walletAddress && walletAddress !== '-') {
            valueToCopy = walletAddress;
        }
        navigator.clipboard.writeText(valueToCopy);
    };

    React.useEffect(() => {
        dispatchFormAction({ type: 'ACCOUNT_CHANGED' });
    }, [values.Recipient?.value.Account]);

    React.useEffect(() => {
        if (!formState.AccountAddressValidated) {
            validateForm();
        }
    }, [formState.AccountAddressValidated]);

    const accountAddressChanged = () => {
        setFieldValue('Recipient', null);
        setFieldValue('RecipientAccountName', '');
        setFieldValue('RecipientFirmName', '');
        setFieldValue('RecipientAccountId', '');
        setFieldValue('UseAAN', false);
        dispatchFormAction({ type: 'ACCOUNT_CHANGED' });
    };

    React.useEffect(() => {
        if (values.UseAAN) {
            values.PublicDescription = values.Recipient?.value?.AssociatedAccountNumber;
        }
        if (!values.UseAAN && (values.PublicDescription === values.Recipient?.value?.AssociatedAccountNumber)) {
            values.PublicDescription = '';
        }
    }, [ values.UseAAN, values.Recipient ]);

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='row'
                justifyContent='space-around'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    container
                    direction='column'
                    item
                    md={5}
                    spacing={0}
                    wrap='nowrap'
                    xs={12}
                >
                    <Grid
                        className={classes.fieldContainer}
                        container
                        direction='column'
                        item
                        spacing={0}
                        wrap='nowrap'
                        xs={12}
                    >
                        <Grid
                            container
                            direction='column'
                            spacing={0}
                            wrap='nowrap'
                        >
                            <Typography
                                className={classes.SelectAssetDropdownTitle}
                                variant='h5'
                            >
                                <strong>Select Asset</strong>
                            </Typography>
                            <SelectField
                                autoComplete={false}
                                errors={errors}
                                id='input_AssetTypeDropdown'
                                label=''
                                name='AssetType'
                                options={assetOptions}
                                placeholder='Select Asset'
                                values={values}
                            />
                            {(values.AssetType && values.AssetType.value === values.AssetType.refData?.currency?.Ticker) ?
                                <>
                                    <Typography
                                        className={classes.SelectAssetDropdownTitle}
                                        variant='h5'
                                    >
                                        <strong>Send Amount</strong>
                                    </Typography>
                                    <CurrencyField
                                        currencyDecimalPoint={2}
                                        disabled={disableFiatField}
                                        errors={errors}
                                        fieldSize='large'
                                        label=''
                                        name='FiatAmount'
                                        placeholder={values.AssetType.refData?.currency?.Ticker ?? 'USD'}
                                    />
                                    <Typography
                                        className={classes.AvailableBalance}
                                        variant='h5'
                                    >
                                        {`Available ${values.AssetType.refData?.currency?.Ticker} Balance: ${formatAsAsset(accountPositionCcyBalance?.availableBalance, values.AssetType.refData?.currency, 'symbol')}`}
                                    </Typography>
                                </>
                                : (values.AssetType && values.AssetType.value === values.AssetType.refData?.security?.Ticker) ?
                                    <>
                                        <Typography
                                            className={classes.SelectAssetDropdownTitle}
                                            variant='h5'
                                        >
                                            <strong>Send Amount</strong>
                                        </Typography>
                                        <CurrencyField
                                            currencyDecimalPoint={2}
                                            disabled={disableFiatField}
                                            errors={errors}
                                            fieldSize='large'
                                            label=''
                                            name='FiatAmount'
                                            placeholder={values.AssetType.refData?.currency?.Ticker ?? 'USD'}
                                        />
                                        <Typography
                                            className={classes.AssetEquivalency}
                                            variant='h2'
                                        >
                                            <strong>{'\u2248'}</strong>
                                        </Typography>
                                        <CurrencyField
                                            currencyDecimalPoint={6}
                                            currencySymbol={values.AssetType.refData?.security?.Ticker ?? ''}
                                            disabled={disableAssetField}
                                            errors={errors}
                                            fieldSize='large'
                                            label=''
                                            name='AssetAmount'
                                            placeholder={values.AssetType.refData?.security?.Ticker ?? ''}
                                        />
                                        <Typography
                                            className={classes.AvailableBalance}
                                            variant='h5'
                                        >
                                            {`Available ${values.AssetType.refData?.security?.Ticker} Balance: ${formatAsAssetNoSign(accountPositionSecurityBalance?.availableBalance, values.AssetType.refData?.security, true)} \u2248
                                             ${formatAsAsset((accountPositionSecurityBalance ? (Number(accountPositionSecurityBalance?.availableBalance) * Number(values.AssetType.refData?.security.Price)) : 0), values.AssetType.refData?.currency, 'symbol')}`}
                                        </Typography>
                                    </>
                                    : <></>}
                        </Grid>
                    </Grid>
                    <Grid
                        className={classes.fieldContainer}
                        container
                        direction='column'
                        item
                        spacing={0}
                        wrap='nowrap'
                        xs={12}
                    >
                        <Typography
                            className={classes.SelectAssetDropdownTitle}
                            variant='h5'
                        >
                            <strong>Purpose</strong>
                        </Typography>
                        <SelectField
                            autoComplete={false}
                            errors={errors}
                            id='input_AssetTypeDropdown'
                            label=''
                            name='Purpose'
                            options={SendAssetPurposeMapped}
                            placeholder='Select Purpose for Send'
                            values={values}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    direction='column'
                    item
                    md={5}
                    spacing={0}
                    wrap='nowrap'
                    xs={12}
                >
                    <Grid
                        className={classes.fieldContainer}
                        item
                    >
                        <RecipientField
                            addressBookOptions={filteredContacts}
                            components={{
                                Option: FredAddressBookContactOptionRenderer,
                                DropdownIndicator: AddressBookDropdown
                            }}
                            disabled={validatingAccountAddress}
                            errors={errors}
                            label='To Recipient'
                            name='RecipientAddress'
                            placeholder='Select Recipient or Enter Lynq Account Address'
                            values={{
                                Recipient: values.Recipient as SelectOption,
                                RecipientAddress: values.RecipientAddress
                            }}
                            walletAddressChanged={accountAddressChanged}
                        />
                        {(values.Recipient || formState.AccountAddressDetails.AccountName) &&
                            <>
                                <Typography
                                    className={classes.RecipientTitle}
                                    variant='h5'
                                >
                                    Firm:
                                </Typography>
                                <Typography
                                    className={classes.RecipientData}
                                    variant='h5'
                                >
                                    {values.Recipient?.value.FirmName || formState.AccountAddressDetails.FirmName}
                                </Typography>
                                <Typography
                                    className={classes.RecipientTitle}
                                    variant='h5'
                                >
                                    Account:
                                </Typography>
                                <Grid
                                    className={classes.RecipientData}
                                    item
                                >
                                    <WalletAddress
                                        buttonType={ButtonType.SMALL}
                                        isAdmin={false}
                                        walletAddress={values.Recipient?.value.Account || formState.AccountAddressDetails.Account}
                                    />
                                </Grid>
                                {values.Recipient?.value?.AssociatedAccountNumber &&
                                    <>
                                        <Typography
                                            className={classes.RecipientTitle}
                                            variant='h5'
                                        >
                                            Associated Account Number:
                                        </Typography>
                                        <Typography
                                            className={classes.RecipientData}
                                            variant='h5'
                                        >
                                            {values.Recipient?.value?.AssociatedAccountNumber}
                                        </Typography>
                                    </>}
                            </>}
                        {(values.RecipientAddress && !errors.Recipient && !errors.RecipientAddress) && (
                            <>
                                <br />
                                <br />
                                <LastTwoAssetTransactionsWidget
                                    assets={assets}
                                    getTransactionsSync={getTransactionsSync}
                                    recipientAddress={values.RecipientAddress}
                                    selectedWallet={currentWallet}
                                    type='S'
                                />
                            </>
                        )}

                    </Grid>
                    <Grid
                        className={classes.fieldContainer}
                        container
                        direction='column'
                        item
                        spacing={0}
                        wrap='nowrap'
                        xs={12}
                    >
                        {values.Recipient?.value?.AssociatedAccountNumber &&
                            <Grid
                                className={classes.checkboxField}
                                container
                                direction='row'
                                item
                                xs={12}
                            >
                                <CheckboxField
                                    errors={errors}
                                    label=''
                                    name='UseAAN'
                                    values={values}
                                />
                                <Typography
                                    className={classes.checkboxFieldTitle}
                                    variant='h5'
                                >
                                    Use Associated Account Number
                                </Typography>
                            </Grid>}
                        <TextField
                            errors={errors}
                            fieldSize='large'
                            label='Public Description (Optional)'
                            maxLength={40}
                            name='PublicDescription'
                            values={values}
                        />
                        <TextField
                            errors={errors}
                            fieldSize='large'
                            label='Private Description (Optional)'
                            maxLength={40}
                            name='PrivateDescription'
                            values={values}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                className={classes.submitContainer}
                container
                direction='column'
                item
                justifyContent='center'
                spacing={0}
                wrap='nowrap'
                xs={12}
            >
                <SubmitButton
                    disabled={isSubmitting || !isValid ||
                        (values.AssetType &&
                            values.AssetType?.value === values.AssetType.refData?.security?.Ticker && !values.AssetType.refData)}
                    onClick={submitForm}
                    text='SEND'
                    type='send-token'
                />
                {(values.AssetType &&
                    values.AssetType?.value === values.AssetType.refData?.security?.Ticker && !values.AssetType.refData) &&
                    <SimpleErrorMessage
                        className={classes.submitError}
                        error={`${values.AssetType.refData?.security?.Ticker} transfers unavailable at this time`}
                    />}
            </Grid>
            {/* TODO Update legal text when multiple asset send is enabled - The current text is only for TFND */}
            <Typography className={classes.legalText}>
                {'By pushing SEND above, I hereby certify that the information previously provided to Arca Institutional U.S. Treasury Fund in my Subscription Agreement (TFND), AML/KYC questionnaire and/or other documents provided to the TFND, as well as all account information, agreements and representations provided to tZERO Securities, LLC, remains accurate and complete in all respects as of the date hereof. Click here to access '}
                <a
                    href='https://storage.googleapis.com/lynq-docs/pdfs/arca_tfnd_ppm.pdf'
                    rel='noreferrer'
                    target='_blank'
                >
                    TFND&apos;s Private Placement Memorandum and Financial Statements.
                </a>
            </Typography>
        </form>
    );
};

export default function SendAssetForm(props: IFormikProps) {
    const {
        classes,
        formProps
    } = props;

    const {
        currentWallet,
        handleSend,
        validateWalletAddressWithCore,
        accountPositionBalance
    } = formProps;

    const [initialValues] = React.useState<ISendFormValues>({
        AssetType: emptySelectOption,
        Type: TransactionTypeChar.send,
        FiatAmount: '',
        AssetAmount: '',
        isFiatAmount: false,
        Sender: currentWallet,
        Recipient: null,
        RecipientAddress: '',
        RecipientAccountName: '',
        RecipientFirmName: '',
        RecipientAccountId: '',
        RecipientFirmId: '',
        Purpose: emptySelectOption,
        PublicDescription: '',
        PrivateDescription: '',
        NeedsApproval: false,
        RequestTime: null,
        UseAAN: false
    });

    const [
        formState,
        dispatchFormAction
    ] = React.useReducer<React.Reducer<
        SendAssetFormState,
        SendAssetFormAction
    >>(SendAssetFormReducer, DEFAULT_INITIAL_STATE);

    if (!currentWallet) return <div />;

    const sendFormOriginalValidations = async (values: ISendFormValues) => {
        const errors: IFormErrors = {} as IFormErrors;
        try {
            await validateSend(values, {
                accountPositionBalance,
                errors,
                currentWallet,
                formState,
                dispatchFormAction,
                validateWalletAddressWithCore
            });
        } catch (error){
            errors.RecipientAddress = 'Could not validate recipient';
        }

        return errors;
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values: ISendFormValues) => {
                values.NeedsApproval = currentWallet.AccessControl;
                values.RequestTime = moment();

                if (formState.AccountAddressDetails.Account) {
                    values.RecipientAccountId = formState.AccountAddressDetails.AccountId;
                    values.RecipientAccountName = formState.AccountAddressDetails.AccountName;
                    values.RecipientFirmName = formState.AccountAddressDetails.FirmName;
                    values.RecipientFirmId = formState.AccountAddressDetails.FirmId;
                }

                if (handleSend) {
                    handleSend(values);
                }
            }}
            validate={sendFormOriginalValidations}
            validateOnChange
            validateOnMount
        >
            {
                (formContentsProps: IFormProps) => (
                    <FormContents
                        {...formContentsProps}
                        accountPositionBalance={accountPositionBalance}
                        classes={classes}
                        dispatchFormAction={dispatchFormAction}
                        formProps={formProps}
                        formState={formState}
                    />
                )
            }
        </Formik>
    );
}